/**
 * The rest-over sounds (`D209`, B8): Padrão, Sino and Sucesso, synthesised
 * with Web Audio at the moment they play.
 *
 * ── MADE HERE, NOT SHIPPED ─────────────────────────────────────────────────
 * Three short tones need no asset, no licence and no download: each is a few
 * oscillators and an envelope, so the bundle carries a dozen numbers rather
 * than three files, and nothing is fetched mid-rest. `@repo/client/session`'s
 * `TIMER_DONE_SOUND_URI` was the Expo app's chime; this replaces it on the
 * new app, where nothing played at all.
 *
 * - Padrão: two short beeps, 880 Hz — the kitchen timer everyone knows.
 * - Sino: a struck bell at 660 Hz with two partials, decaying over a second.
 * - Sucesso: a rising C-major arpeggio, C5 E5 G5 C6.
 *
 * And two cues of the set itself (`D243`), made the same way:
 *
 * - A set logged: one soft tick, a 1 kHz sine for 60 ms — an answer to the
 *   tap, quieter than any alarm, so thirty of them in a workout never nag.
 * - A new record: a short fanfare, G5 C6 E6 and a held G6 over a fifth
 *   below it — brighter and longer than "Sucesso", so the two are never
 *   mistaken across a gym.
 *
 * Both play at the rest's volume and are silent at "Mudo"; the setting
 * "Som ao registrar série e recorde" switches them off alone.
 *
 * ── FOREGROUND ONLY, AND NEVER A THROW ─────────────────────────────────────
 * In the background the notification makes the system's sound (its channel
 * is the person's to tune). A WebView that refuses an `AudioContext` — no
 * user gesture yet, an old engine — loses the sound and nothing else.
 */
import type { RestSound, RestVolume } from "../prefs/workout-prefs";

/** The volumes the setting offers, as gain. `off` plays nothing. */
const GAIN: Readonly<Record<RestVolume, number>> = {
  high: 0.6,
  low: 0.12,
  medium: 0.3,
  off: 0,
};

interface Note {
  /** Seconds from the start. */
  readonly at: number;
  /** Seconds the note sounds before it has decayed. */
  readonly duration: number;
  readonly frequency: number;
  /** Relative to the volume's gain. */
  readonly level: number;
  readonly type: OscillatorType;
}

/** The three sounds as notes — exported so a test can pin their shape. */
export const REST_SOUND_NOTES: Readonly<Record<RestSound, readonly Note[]>> = {
  bell: [
    { at: 0, duration: 1.2, frequency: 660, level: 1, type: "sine" },
    { at: 0, duration: 0.8, frequency: 1320, level: 0.4, type: "sine" },
    { at: 0, duration: 0.5, frequency: 1980, level: 0.2, type: "sine" },
  ],
  default: [
    { at: 0, duration: 0.14, frequency: 880, level: 1, type: "triangle" },
    { at: 0.22, duration: 0.14, frequency: 880, level: 1, type: "triangle" },
  ],
  success: [
    { at: 0, duration: 0.16, frequency: 523.25, level: 1, type: "triangle" },
    { at: 0.11, duration: 0.16, frequency: 659.25, level: 1, type: "triangle" },
    { at: 0.22, duration: 0.16, frequency: 783.99, level: 1, type: "triangle" },
    { at: 0.33, duration: 0.4, frequency: 1046.5, level: 1, type: "triangle" },
  ],
};

/** The set's own cues (`D243`), as notes — exported for the same reason. */
export const SET_CUE_NOTES: Readonly<Record<SetCue, readonly Note[]>> = {
  record: [
    { at: 0, duration: 0.14, frequency: 783.99, level: 0.9, type: "triangle" },
    {
      at: 0.1,
      duration: 0.14,
      frequency: 1046.5,
      level: 0.9,
      type: "triangle",
    },
    {
      at: 0.2,
      duration: 0.14,
      frequency: 1318.51,
      level: 0.9,
      type: "triangle",
    },
    { at: 0.3, duration: 0.7, frequency: 1567.98, level: 1, type: "triangle" },
    { at: 0.3, duration: 0.7, frequency: 1046.5, level: 0.45, type: "sine" },
  ],
  set: [{ at: 0, duration: 0.06, frequency: 1000, level: 0.5, type: "sine" }],
};

export type SetCue = "record" | "set";

let context: AudioContext | null = null;

function audio(): AudioContext | null {
  if (context === null) {
    const Ctor =
      typeof window === "undefined"
        ? undefined
        : (window.AudioContext as typeof AudioContext | undefined);
    if (Ctor === undefined) {
      return null;
    }
    context = new Ctor();
  }
  return context;
}

/** Plays one of the sounds at one of the volumes. Never throws. */
export function playRestSound(sound: RestSound, volume: RestVolume): void {
  playNotes(REST_SOUND_NOTES[sound], volume);
}

/** Plays a set's cue at the rest's volume (`D243`). Never throws. */
export function playSetCue(cue: SetCue, volume: RestVolume): void {
  playNotes(SET_CUE_NOTES[cue], volume);
}

function playNotes(notes: readonly Note[], volume: RestVolume): void {
  const gain = GAIN[volume];
  if (gain === 0) {
    return;
  }
  try {
    const ctx = audio();
    if (ctx === null) {
      return;
    }
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => undefined);
    }
    const start = ctx.currentTime + 0.02;
    for (const note of notes) {
      const oscillator = ctx.createOscillator();
      const envelope = ctx.createGain();
      oscillator.type = note.type;
      oscillator.frequency.value = note.frequency;
      const t0 = start + note.at;
      const peak = gain * note.level;
      envelope.gain.setValueAtTime(0.0001, t0);
      envelope.gain.exponentialRampToValueAtTime(peak, t0 + 0.01);
      envelope.gain.exponentialRampToValueAtTime(0.0001, t0 + note.duration);
      oscillator.connect(envelope).connect(ctx.destination);
      oscillator.start(t0);
      oscillator.stop(t0 + note.duration + 0.05);
    }
  } catch (error) {
    console.warn("[workout] the rest sound did not play", error);
  }
}
